import { resolveAssetUrl, resolvePublicAssetUrl } from '../../lib/assets';
import type { Product } from '../../types';
import type {
    ArCatalogEntry,
    ArTrackingConfig,
    ArTrackingMode,
    ArTransformDefaults,
    ResolvedArExperience,
} from './types';

const sampleModelUrl = '/ar/models/sample-card/scene.gltf';
const sampleImageTarget = '/ar/targets/sample-card/card.mind';
const samplePreviewImage = '/ar/targets/sample-card/card.png';

const defaultTransform: ArTransformDefaults = {
    scale: '0.005 0.005 0.005',
    position: '0 0 0.08',
    rotation: '0 0 0',
    centerPivot: true,
    alignY: 'floor',
    enableShadow: true,
};

const defaultCatalogEntry: ArCatalogEntry = {
    configKey: 'default-image-demo',
    itemSlug: 'default-ar-item',
    itemName: 'Produto AR',
    trackingMode: 'image-tracking',
    imageTargetSrc: sampleImageTarget,
    previewImageUrl: samplePreviewImage,
    targetIndex: 0,
    modelUrl: sampleModelUrl,
    instruction: 'Aponte a camera para a imagem-alvo cadastrada.',
    shadowEnabled: true,
    scale: defaultTransform.scale,
    position: defaultTransform.position,
    rotation: defaultTransform.rotation,
    centerPivot: true,
    alignY: 'floor',
};

const markerFallbackEntry: ArCatalogEntry = {
    ...defaultCatalogEntry,
    configKey: 'default-marker-demo',
    trackingMode: 'marker-tracking',
    markerId: 'hiro',
    markerPreset: 'hiro',
    instruction: 'Aponte a camera para o marcador configurado.',
    scale: '0.03 0.03 0.03',
    position: '0 0 0',
    rotation: '0 0 0',
};

export const arCatalog: ArCatalogEntry[] = [
    defaultCatalogEntry,
    markerFallbackEntry,
];

const normalizeTransform = (entry: ArCatalogEntry): ArTransformDefaults => ({
    scale: entry.scale || defaultTransform.scale,
    position: entry.position || defaultTransform.position,
    rotation: entry.rotation || defaultTransform.rotation,
    centerPivot: entry.centerPivot ?? defaultTransform.centerPivot,
    alignY: entry.alignY || defaultTransform.alignY,
    enableShadow: entry.shadowEnabled ?? defaultTransform.enableShadow,
});

const getDefaultEntryForMode = (mode: ArTrackingMode) =>
    mode === 'image-tracking' ? defaultCatalogEntry : markerFallbackEntry;

const matchesCatalogEntry = (entry: ArCatalogEntry, slug: string, product?: Product | null, configKey?: string) => {
    if (configKey) {
        return entry.configKey === configKey;
    }

    if (entry.storeSlug && entry.storeSlug !== slug) {
        return false;
    }

    if (!product) {
        return false;
    }

    if (entry.productId && entry.productId === product.id) {
        return true;
    }

    if (entry.productName && entry.productName.toLowerCase() === product.name.toLowerCase()) {
        return true;
    }

    return false;
};

const pickProduct = (products: Product[], productId?: string) => {
    if (productId) {
        const exact = products.find((product) => product.id === productId);
        if (exact) {
            return exact;
        }
    }

    return products.find((product) => product.model3dEnabled && product.model3dGlbUrl) || products[0];
};

const buildRuntimeConfig = ({
    entry,
    product,
    slug,
}: {
    entry: ArCatalogEntry;
    product?: Product;
    slug: string;
}): ArTrackingConfig => {
    const transform = normalizeTransform(entry);
    const modelUrl = product?.model3dEnabled && product.model3dGlbUrl
        ? resolveAssetUrl(product.model3dGlbUrl)
        : resolvePublicAssetUrl(entry.modelUrl || sampleModelUrl);

    const bindingBase = {
        configKey: entry.configKey,
        itemId: product?.id || entry.productId || entry.configKey,
        itemName: product?.name || entry.itemName,
        itemSlug: entry.itemSlug,
        productId: product?.id || entry.productId,
        staticModelUrl: modelUrl,
        fallbackStaticModelUrl: resolvePublicAssetUrl(entry.modelUrl || sampleModelUrl),
        posterUrl: product?.imageUrl ? resolveAssetUrl(product.imageUrl) : undefined,
        transform,
    };

    if (entry.trackingMode === 'marker-tracking') {
        return {
            id: `${slug}-${entry.configKey}`,
            mode: 'marker-tracking',
            targetName: entry.markerId || entry.markerPreset || 'Marcador impresso',
            instruction: entry.instruction || markerFallbackEntry.instruction || '',
            markerId: entry.markerId || entry.markerPreset || 'marker',
            markerPreset: entry.markerPreset,
            markerPatternUrl: entry.markerPatternUrl ? resolvePublicAssetUrl(entry.markerPatternUrl) : undefined,
            binding: bindingBase,
        };
    }

    return {
        id: `${slug}-${entry.configKey}`,
        mode: 'image-tracking',
        targetName: entry.itemName,
        instruction: entry.instruction || defaultCatalogEntry.instruction || '',
        imageTargetSrc: resolvePublicAssetUrl(entry.imageTargetSrc || sampleImageTarget),
        previewImageUrl: entry.previewImageUrl ? resolvePublicAssetUrl(entry.previewImageUrl) : undefined,
        targetIndex: entry.targetIndex ?? 0,
        binding: bindingBase,
    };
};

export const resolveArExperience = ({
    slug,
    mode,
    products,
    productId,
    configKey,
}: {
    slug: string;
    mode: ArTrackingMode;
    products: Product[];
    productId?: string;
    configKey?: string;
}): ResolvedArExperience => {
    const product = pickProduct(products, productId);
    const matchedEntry = arCatalog.find((entry) => entry.trackingMode === mode && matchesCatalogEntry(entry, slug, product, configKey))
        || getDefaultEntryForMode(mode);
    const runtimeConfig = buildRuntimeConfig({ entry: matchedEntry, product, slug });

    return {
        slug,
        configKey: matchedEntry.configKey,
        mode: runtimeConfig.mode,
        targetId: runtimeConfig.id,
        targetName: runtimeConfig.targetName,
        instruction: runtimeConfig.instruction,
        previewImageUrl: 'previewImageUrl' in runtimeConfig ? runtimeConfig.previewImageUrl : undefined,
        modelUrl: runtimeConfig.binding.staticModelUrl || runtimeConfig.binding.fallbackStaticModelUrl || resolvePublicAssetUrl(sampleModelUrl),
        itemId: runtimeConfig.binding.itemId,
        itemName: runtimeConfig.binding.itemName,
        sourceProduct: product,
        transform: runtimeConfig.binding.transform,
        runtimeConfig,
    };
};

export const getTrackingModeInstructions = (mode: ArTrackingMode) => {
    if (mode === 'marker-tracking') {
        return [
            'Imprima o marcador configurado e mantenha-o inteiro no enquadramento.',
            'Evite reflexos e inclinacoes bruscas para manter a estabilidade do tracking.',
            'Use o preset Hiro ou um .patt proprio cadastrado no catalogo AR.',
        ];
    }

    return [
        'Use uma imagem-alvo com bom contraste e area impressa suficiente.',
        'Aproxime devagar ate o alvo ocupar boa parte da tela.',
        'Mantenha luz uniforme para o objeto 3D permanecer ancorado.',
    ];
};
